<template>
    <div class="ranking-users-report">
        <div class="card shadow-sm border-0">

            <HeaderFilter
                title="Ranking de Usuários"
                @apply-filters="loadData"
            />

            <div class="card-body">
                <!-- Loading -->
                <div
                    v-if="loadingData"
                    class="text-center py-4"
                >
                    <i class="fas fa-spinner fa-spin fa-2x text-warning"></i>
                    <p class="mt-2">Carregando ranking...</p>
                </div>

                <!-- Conteúdo Principal -->
                <div v-else>
                    <!-- Top 3 Usuários -->
                    <div
                        class="top-users mb-4"
                        v-if="tableData.length > 0"
                    >
                        <div class="row">
                            <!-- 2º Lugar -->
                            <div
                                class="col-md-4"
                                v-if="tableData[1]"
                            >
                                <div class="podium-card second-place">
                                    <div class="medal-icon">
                                        <i class="fas fa-medal"></i>
                                        <span class="position">2º</span>
                                    </div>
                                    <div class="user-info">
                                        <div class="user-name">{{ tableData[1].name }}</div>
                                        <div class="user-count">{{ tableData[1].total }} reservas</div>
                                    </div>
                                </div>
                            </div>

                            <!-- 1º Lugar -->
                            <div
                                class="col-md-4"
                                v-if="tableData[0]"
                            >
                                <div class="podium-card first-place">
                                    <div class="medal-icon">
                                        <i class="fas fa-crown"></i>
                                        <span class="position">1º</span>
                                    </div>
                                    <div class="user-info">
                                        <div class="user-name">{{ tableData[0].name }}</div>
                                        <div class="user-count">{{ tableData[0].total }} reservas</div>
                                    </div>
                                    <div class="winner-badge">
                                        <i class="fas fa-trophy"></i>
                                        CAMPEÃO
                                    </div>
                                </div>
                            </div>

                            <!-- 3º Lugar -->
                            <div
                                class="col-md-4"
                                v-if="tableData[2]"
                            >
                                <div class="podium-card third-place">
                                    <div class="medal-icon">
                                        <i class="fas fa-medal"></i>
                                        <span class="position">3º</span>
                                    </div>
                                    <div class="user-info">
                                        <div class="user-name">{{ tableData[2].name }}</div>
                                        <div class="user-count">{{ tableData[2].total }} reservas</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Estatísticas Rápidas -->
                    <div
                        class="quick-stats mb-4"
                        v-if="tableData.length > 0"
                    >
                        <div class="row">
                            <div class="col-md-3">
                                <div class="stat-item">
                                    <div class="stat-value">{{ tableData.length }}</div>
                                    <div class="stat-label">Total de Usuários</div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="stat-item">
                                    <div class="stat-value">{{ totalReservations }}</div>
                                    <div class="stat-label">Total de Reservas</div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="stat-item">
                                    <div class="stat-value">{{ avgReservationsPerUser }}</div>
                                    <div class="stat-label">Média por Usuário</div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="stat-item">
                                    <div class="stat-value">{{ topUserPercentage }}%</div>
                                    <div class="stat-label">% do 1º Colocado</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Gráfico -->
                    <div
                        class="chart-container mb-4"
                        v-if="tableData.length > 0"
                    >
                        <GChart
                            type="ColumnChart"
                            :data="chartData"
                            :options="chartOptions"
                            style="width: 100%; height: 300px;"
                        />
                    </div>

                    <!-- Tabela Completa -->
                    <div class="ranking-table">
                        <div class="table-responsive">
                            <table class="table table-hover">
                                <thead class="bg-light">
                                <tr>
                                    <th width="10%">Posição</th>
                                    <th width="50%">Usuário</th>
                                    <th
                                        width="20%"
                                        class="text-center"
                                    >Reservas
                                    </th>
                                    <th
                                        width="20%"
                                        class="text-center"
                                    >Participação
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="(user, index) in tableData"
                                    :key="index"
                                >
                                    <td>
                                        <div
                                            class="position-badge"
                                            :class="getPositionClass(index + 1)"
                                        >
                                            <span v-if="index < 3">
                                                <i :class="getPositionIcon(index + 1)"></i>
                                            </span>
                                            {{ index + 1 }}º
                                        </div>
                                    </td>
                                    <td>
                                        <div class="user-info-row">
                                            <div class="user-avatar">
                                                {{ getUserInitials(user.name) }}
                                            </div>
                                            <div class="user-details">
                                                <div class="user-name">{{ user.name }}</div>
                                                <div class="user-meta text-muted">
                                                    {{ getPartipationText(user.total) }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-center">
                                        <span class="badge badge-primary">{{ user.total }}</span>
                                    </td>
                                    <td class="text-center">
                                        <div class="participation-bar">
                                            <div class="participation-progress">
                                                <div
                                                    class="progress-fill"
                                                    :style="`width: ${getParticipationPercentage(user.total)}%`"
                                                ></div>
                                            </div>
                                            <span class="participation-text">
                                                {{ getParticipationPercentage(user.total) }}%
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="tableData.length === 0">
                                    <td
                                        colspan="4"
                                        class="text-center text-muted py-4"
                                    >
                                        Nenhum usuário encontrado para o período selecionado
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {GChart} from "vue-google-charts";
import vSelect from "vue-select";
import {getMessage} from "../../composables/message";
import HeaderFilter from "./HeaderFilter.vue";

export default {
    name: "RankingUsersReport",

    components: {
        HeaderFilter,
        GChart,
        vSelect,
    },

    props: {
        globalFilters: {
            type: Object,
            default: () => ({})
        },
        loading: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            loadingData: false,

            branches: [],

            chartOptions: {
                chart: {
                    title: "Ranking de Usuários",
                    subtitle: "Usuários com maior número de reservas"
                },
                colors: ["#f49e00", "#1b9e77"],
                hAxis: {
                    title: "Usuários",
                    textStyle: {fontSize: 10}
                },
                vAxis: {
                    title: "Quantidade de Reservas",
                    format: "decimal",
                    minValue: 0
                },
                height: 300,
                legend: {position: "none"},
                bar: {groupWidth: "75%"}
            },

            tableData: [],
        };
    },

    computed: {
        /**
         * Dados formatados para o gráfico / Formatted data for chart
         */
        chartData() {
            if (this.tableData.length === 0) return [["Usuário", "Quantidade"]];

            const chartData = [["Usuário", "Quantidade"]];

            this.tableData.slice(0, 10).forEach(user => {
                chartData.push([this.truncateName(user.name), user.total]);
            });

            return chartData;
        },

        /**
         * Total de reservas / Total reservations
         */
        totalReservations() {
            return this.tableData.reduce((sum, user) => sum + (user.total || 0), 0);
        },

        /**
         * Média de reservas por usuário / Average reservations per user
         */
        avgReservationsPerUser() {
            if (this.tableData.length === 0) return 0;
            return Math.round(this.totalReservations / this.tableData.length);
        },

        /**
         * Percentual do primeiro colocado / Top user percentage
         */
        topUserPercentage() {
            if (this.tableData.length === 0 || this.totalReservations === 0) return 0;
            return Math.round((this.tableData[0].total / this.totalReservations) * 100);
        },
    },

    watch: {
        globalFilters: {
            handler() {
                this.loadData();
            },
            deep: true
        },

        loading(newVal) {
            if (!newVal) {
                this.loadData();
            }
        }
    },

    mounted() {
        this.loadData();
    },

    methods: {
        async loadData(params = null) {
            this.loadingData = true;

            try {
                const api = this.$store.state.api + "rankingUsers";

                const response = await this.$http.get(api, {params});

                this.tableData = response.data.table || [];

                this.$emit('data-updated', 'ranking-users', response.data);

            } catch (error) {
                console.error('Erro ao carregar ranking de usuários:', error);
                const message = error.response.data.errors
                    ? getMessage(error.response.data.errors)
                    : error.response.data || 'Erro ao carregar dados';
                this.$message(null, message, "error");
            } finally {
                this.loadingData = false;
            }
        },

        /**
         * Retorna classe CSS para posição / Return CSS class for position
         */
        getPositionClass(position) {
            if (position === 1) return 'first';
            if (position === 2) return 'second';
            if (position === 3) return 'third';
            return 'other';
        },

        /**
         * Retorna ícone para posição / Return icon for position
         */
        getPositionIcon(position) {
            if (position === 1) return 'fas fa-crown';
            if (position === 2) return 'fas fa-medal';
            if (position === 3) return 'fas fa-medal';
            return '';
        },

        /**
         * Retorna iniciais do usuário / Return user initials
         */
        getUserInitials(name) {
            if (!name) return 'U';
            const parts = name.split(' ');
            if (parts.length >= 2) {
                return parts[0].charAt(0) + parts[1].charAt(0);
            }
            return name.charAt(0);
        },

        /**
         * Retorna texto de participação / Return participation text
         */
        getPartipationText(total) {
            if (total === 1) return '1 reserva';
            return `${total} reservas`;
        },

        /**
         * Calcula percentual de participação / Calculate participation percentage
         */
        getParticipationPercentage(userTotal) {
            if (this.totalReservations === 0) return 0;
            return Math.round((userTotal / this.totalReservations) * 100);
        },

        /**
         * Trunca nome para o gráfico / Truncate name for chart
         */
        truncateName(name) {
            if (!name) return '';
            if (name.length <= 15) return name;
            return name.substring(0, 12) + '...';
        },
    },
};
</script>

<style scoped>
/* Estilo principal do relatório */
.ranking-users-report .card {
    border-radius: 12px;
    overflow: hidden;
}

.bg-gradient-warning {
    background: linear-gradient(135deg, #f6c23e 0%, #d4a934 100%);
}

/* Top 3 usuários - Pódio */
.top-users {
    margin-bottom: 2rem;
}

.podium-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    text-align: center;
    position: relative;
    border: 2px solid #e3e6f0;
    transition: all 0.3s ease;
    height: 200px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.podium-card.first-place {
    border-color: #ffd700;
    background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
    transform: scale(1.05);
}

.podium-card.second-place {
    border-color: #c0c0c0;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.podium-card.third-place {
    border-color: #cd7f32;
    background: linear-gradient(135deg, #fdf6f0 0%, #f4e5d3 100%);
}

.medal-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
    position: relative;
}

.first-place .medal-icon {
    color: #ffd700;
}

.second-place .medal-icon {
    color: #c0c0c0;
}

.third-place .medal-icon {
    color: #cd7f32;
}

.position {
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    background: #FE8D18;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: bold;
}

.user-name {
    font-weight: 700;
    font-size: 1.1rem;
    color: #2d3748;
}

.user-count {
    color: #718096;
    font-weight: 600;
}

.winner-badge {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    color: #b45309;
    padding: 0.25rem 0.75rem;
    border-radius: 15px;
    font-size: 0.75rem;
    font-weight: 700;
    box-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
}

/* Estatísticas rápidas */
.quick-stats {
    background: #f8f9fc;
    border-radius: 8px;
    padding: 1.5rem;
}

.stat-item {
    text-align: center;
}

.stat-value {
    font-size: 1.75rem;
    font-weight: 800;
    color: #2d3748;
    margin-bottom: 0.5rem;
}

.stat-label {
    font-size: 0.875rem;
    color: #718096;
    text-transform: uppercase;
    font-weight: 600;
}

/* Container do gráfico */
.chart-container {
    background: white;
    border-radius: 8px;
    padding: 1rem;
    border: 1px solid #e3e6f0;
}

/* Tabela de ranking */
.ranking-table {
    background: white;
    border-radius: 8px;
    overflow: hidden;
}

.table {
    margin-bottom: 0;
}

.table thead th {
    border-top: none;
    font-weight: 600;
    color: #5a5c69;
    background-color: #f8f9fc;
}

.table tbody tr:hover {
    background-color: #f8f9fc;
}

/* Badge de posição */
.position-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.375rem 0.75rem;
    border-radius: 20px;
    font-weight: 700;
    font-size: 0.875rem;
}

.position-badge.first {
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    color: #b45309;
}

.position-badge.second {
    background: linear-gradient(135deg, #c0c0c0 0%, #d3d3d3 100%);
    color: #495057;
}

.position-badge.third {
    background: linear-gradient(135deg, #cd7f32 0%, #daa520 100%);
    color: #fff;
}

.position-badge.other {
    background: #e9ecef;
    color: #6c757d;
}

/* Informações do usuário na linha */
.user-info-row {
    display: flex;
    align-items: center;
}

.user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    margin-right: 1rem;
    font-size: 0.875rem;
}

.user-details .user-name {
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 0.25rem;
}

.user-meta {
    font-size: 0.75rem;
}

/* Barra de participação */
.participation-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.participation-progress {
    flex: 1;
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transition: width 0.3s ease;
}

.participation-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: #495057;
    min-width: 40px;
}

/* Badges */
.badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
}

/* Formulários */
.form-label {
    font-weight: 600;
    color: #5a5c69;
    margin-bottom: 0.5rem;
}

.form-control {
    border-radius: 0.35rem;
    border: 1px solid #d1d3e2;
}

.form-control:focus {
    border-color: #f6c23e;
    box-shadow: 0 0 0 0.2rem rgba(246, 194, 62, 0.25);
}

/* Responsividade */
@media (max-width: 768px) {
    .podium-card {
        height: 150px;
        margin-bottom: 1rem;
    }

    .podium-card.first-place {
        transform: none;
    }

    .medal-icon {
        font-size: 1.5rem;
    }

    .user-name {
        font-size: 1rem;
    }

    .stat-value {
        font-size: 1.25rem;
    }

    .user-info-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }

    .user-avatar {
        margin-right: 0;
    }

    .participation-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .table-responsive {
        font-size: 0.875rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Indicators/ranking_users.vue